import { CameraOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { Badge } from 'antd';
import { BuyModal } from 'components/Modals/BuyModal';
import { ShowMoreButton } from 'components/UI/ProductCard/atoms';
import CopyableCode from 'components/UI/ProductCard/atoms/Typography/CopyableCode';
import {
  ActionButtons,
  OfferInfo,
  PriceInfo,
  ProductCardActions,
  ProductHeader,
  QualityInfo,
} from 'components/UI/ProductCard/molecules';
import ProductCardImage from 'components/UI/ProductCardImage';
import { RealPhotosLegend } from 'components/UI/ProductCardImageSimple.styled';
import { useCompositionButton } from 'hooks/useCompositionButton';
import { useBreakpoints } from 'hooks/useMediaQuery';
import { useShippingFee } from 'hooks/useShippingFee';
import { noop } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as sessionSelectors from 'store/state/session/sessionSelectors';
import { ProductCardGridProps } from '../ProductCardGrid';
import {
  CaretContainer,
  ProductCardCompactContainer,
  ProductCompactActionsCol,
  ProductCompactContentSection,
  ProductCompactDetails,
  ProductCompactExpandedContent,
  ProductCompactExpandedDivider,
  ProductCompactExpandedItem,
  ProductCompactExpandedObservation,
  ProductCompactExpandedRow,
  ProductCompactExpandedSection,
  ProductCompactImageSection,
  ProductCompactOfferRow,
  ProductCompactPriceRow,
  ProductCompactQualityRow,
  ProductCompactQualitySection,
  ProductCompactTitleRow,
} from './ProductCardCompact.styled';

export type ProductCardCompactProps = Omit<
  ProductCardGridProps,
  'onBuyClick'
> & {
  addInCart: (
    quantityPacking: number,
    selectedTypeSale: any,
  ) => void | Promise<void>;
  buy: (quantity: number, selectedTypeSale: any) => void | Promise<void>;
};

export function ProductCardCompact({
  product,
  packingSelected,
  getLowPrice,
  onDetailsClick,
  buy,
  disabled = false,
  addInCart,
  showModalComposition,
  onSelectPacking,
  isExclusiveOffer = false,
}: ProductCardCompactProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDesktop, isNotDesktop, isTablet } = useBreakpoints();

  const canShowComposition = useCompositionButton(product);
  const { shippingFee } = useShippingFee(product);

  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);
  const customerSelected = useSelector(sessionSelectors.getSelectedCustomer);

  const shouldShowCartButton = Boolean(
    isAuthenticated && customerSelected && !disabled && addInCart,
  );

  const { offerType } = product;
  const isLkpOffer = useMemo(
    () => offerType === 1 || offerType === 2,
    [offerType],
  );
  const isEnpOffer = useMemo(
    () => offerType === 0 || offerType === 3,
    [offerType],
  );

  const detailsButtonText = useMemo(() => {
    if (!isTablet) {
      return 'Abrir';
    }

    if (isTablet && !isDesktop) {
      return isLkpOffer ? 'Detalhes | GFPS' : 'Detalhes';
    }

    return isLkpOffer ? 'Detalhes | Comprar por GFP' : 'Detalhes';
  }, [isLkpOffer, isTablet, isDesktop]);

  const handleShowMoreClick = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const [addToCartModalOpen, setAddToCartModalOpen] = useState(false);

  const handleBuyClick = useCallback(() => {
    setBuyModalOpen(true);
  }, []);

  const handleBuyModalClose = useCallback(() => {
    setBuyModalOpen(false);
  }, []);

  const handleAddToCartModalOpen = useCallback(() => {
    setAddToCartModalOpen(true);
  }, []);

  const handleAddToCartModalClose = useCallback(() => {
    setAddToCartModalOpen(false);
  }, []);

  const handleBuyConfirm = useCallback(
    (purchaseData) => {
      setBuyModalOpen(false);
      if (buy) {
        const { quantity, purchaseType } = purchaseData;
        buy(quantity, purchaseType);
      }
    },
    [buy],
  );

  const handleAddToCartConfirm = useCallback(
    (purchaseData) => {
      setAddToCartModalOpen(false);
      if (addInCart) {
        const { quantity, purchaseType } = purchaseData;
        addInCart(quantity, purchaseType);
      }
    },
    [addInCart],
  );

  const handleAddToCartMobile = useCallback(() => {
    handleAddToCartModalOpen();
  }, [handleAddToCartModalOpen]);

  const handleAddToCartDesktop = useCallback(
    (quantity: number, saleType: any) => {
      if (addInCart) {
        return addInCart(quantity, saleType);
      }
    },
    [addInCart],
  );

  const handleBuyNowDesktop = useCallback(
    (quantity: number, saleType: any) => {
      if (buy) {
        return buy(quantity, saleType);
      }
    },
    [buy],
  );

  const { isTabletMiniOrLarger } = useBreakpoints();

  const isDirectedOffer = product.offerType === 3;
  const hasSpecialPrice =
    !Number.isNaN(product.specialPrice) && product.specialPrice > 0;
  const hasValidReajustment =
    !Number.isNaN(product.reajustmentAmount) && product.reajustmentAmount !== 0;

  const shouldShowBadge = isDirectedOffer
    ? hasSpecialPrice
    : hasValidReajustment;

  let badgeText = '';
  if (isDirectedOffer && hasSpecialPrice) {
    badgeText = `Oferta direcionada R$${Math.abs(product.specialPrice)
      .toFixed(2)
      .replace('.', ',')}`;
  } else if (product.voucherReajustmentType === 'P') {
    badgeText = `${Math.abs(product.reajustmentAmount)}%`;
  } else {
    badgeText = `R$${Math.abs(product.reajustmentAmount)
      .toFixed(2)
      .replace('.', ',')}`;
  }

  return (
    <Badge.Ribbon
      text={badgeText}
      placement="start"
      color="green"
      style={{ display: shouldShowBadge ? 'block' : 'none' }}
    >
      <ProductCardCompactContainer className="product-card-compact-container">
        <ProductCompactImageSection className="product-compact-image-section">
          {product.imagesFormatted?.[0] && (
            <ProductCardImage
              images={product.imagesFormatted}
              alt={`Imagem de ${product.longName}`}
              isLkpOffer={isLkpOffer}
              showNavigation={false}
              showLabel={false}
              productName={product.longName}
            />
          )}
        </ProductCompactImageSection>
        <ProductCompactContentSection className="product-compact-content-section">
          <ProductCompactTitleRow className="product-title-row">
            <ProductHeader
              product={product}
              isExclusiveOffer={isExclusiveOffer}
            />
            {isTabletMiniOrLarger && (
              <OfferInfo
                className="product-offer-info-desktop"
                product={product}
                smallFormat={false}
              />
            )}
          </ProductCompactTitleRow>
          {!isTabletMiniOrLarger && (
            <ProductCompactOfferRow>
              <OfferInfo product={product} />
            </ProductCompactOfferRow>
          )}
          <ProductCompactDetails className="product-compact-details">
            <ProductCompactQualityRow>
              <ProductCompactQualitySection>
                <QualityInfo
                  product={product}
                  packingSelected={packingSelected}
                  showGiftIcon={false}
                  radius="2px"
                  onSelectPacking={onSelectPacking}
                />
                <ProductCompactExpandedItem className="desktop-multiple">
                  <strong>Múltiplo:</strong>
                  <span>{packingSelected?.multiple ?? 'N/A'}</span>
                </ProductCompactExpandedItem>
              </ProductCompactQualitySection>
              <ShowMoreButton onClick={handleShowMoreClick} disabled={disabled}>
                {isExpanded ? 'Exibir menos' : 'Exibir mais'}
                <CaretContainer>
                  {isExpanded && <UpOutlined />}
                  {!isExpanded && <DownOutlined />}
                </CaretContainer>
              </ShowMoreButton>
            </ProductCompactQualityRow>
            <ProductCompactExpandedSection isExpanded={isExpanded}>
              <ProductCompactExpandedContent>
                <ProductCompactExpandedRow>
                  <ProductCompactExpandedItem hideOnDesktop>
                    <strong>Múltiplo:</strong>
                    <span>{packingSelected?.multiple ?? 'N/A'}</span>
                  </ProductCompactExpandedItem>
                  <ProductCompactExpandedItem>
                    <strong>Dimensão/Altura:</strong>
                    <span>{product.dimension || 'N/A'}</span>
                  </ProductCompactExpandedItem>
                  <ProductCompactExpandedItem>
                    <strong>Cores:</strong>
                    <span>{product.colors || 'N/A'}</span>
                  </ProductCompactExpandedItem>
                  <ProductCompactExpandedItem>
                    <strong>Cód. do Produto</strong>
                    <CopyableCode value={product.barcode} />
                  </ProductCompactExpandedItem>
                </ProductCompactExpandedRow>
                <ProductCompactExpandedRow>
                  <ProductCompactExpandedItem>
                    <strong>Cód. de Barras:</strong>
                    <CopyableCode value={product.gtincode} />
                  </ProductCompactExpandedItem>
                </ProductCompactExpandedRow>
                <ProductCompactExpandedObservation>
                  <strong>Observação:</strong>{' '}
                  {product.observation || 'Nenhuma observação disponível'}
                </ProductCompactExpandedObservation>
                <ProductCompactExpandedDivider />
              </ProductCompactExpandedContent>
            </ProductCompactExpandedSection>
          </ProductCompactDetails>

          {isLkpOffer &&
            (Array.isArray(product.gfpImages)
              ? product.gfpImages.length > 0
              : !!product.gfpImages) && (
              <RealPhotosLegend>
                <CameraOutlined />
                Oferta com fotos reais. Veja nos detalhes (GFPs).
              </RealPhotosLegend>
            )}

          <ProductCompactPriceRow className="product-compact-price-row">
            <PriceInfo price={getLowPrice} unit="un" shipping={shippingFee} />
            {isNotDesktop && (
              <ActionButtons
                onDetailsClick={onDetailsClick}
                onBuyClick={handleBuyClick}
                onAddToCart={
                  shouldShowCartButton ? handleAddToCartMobile : noop
                }
                disabled={disabled}
                buyText=""
                detailsTextMobile={detailsButtonText}
                disableBuyButton={isEnpOffer}
              />
            )}
          </ProductCompactPriceRow>
        </ProductCompactContentSection>
        {isDesktop && (
          <ProductCompactActionsCol>
            <ProductCardActions
              product={product}
              packingSelected={packingSelected}
              disabled={disabled}
              disableBuyButton={isEnpOffer}
              onShowDetails={onDetailsClick || noop}
              onBuyNow={handleBuyNowDesktop}
              onAddToCart={handleAddToCartDesktop}
              onComposition={showModalComposition}
              showCompositionButton={canShowComposition}
              showCartText={false}
            />
          </ProductCompactActionsCol>
        )}
        {isNotDesktop && buyModalOpen && (
          <BuyModal
            visible={buyModalOpen}
            onCancel={handleBuyModalClose}
            onConfirm={handleBuyConfirm}
            product={product}
            variant="buy"
          />
        )}
        {isNotDesktop && addToCartModalOpen && (
          <BuyModal
            visible={addToCartModalOpen}
            onCancel={handleAddToCartModalClose}
            onConfirm={handleAddToCartConfirm}
            product={product}
            variant="addToCart"
          />
        )}
      </ProductCardCompactContainer>
    </Badge.Ribbon>
  );
}
